// app/features/templates/components/TemplateListComponent.tsx
"use client";

import { BookOpen, Briefcase, Code, FileText, Lightbulb } from 'lucide-react';
import { useQueryState } from 'nuqs';
import React from 'react';

import { HTFButton } from '@/app/shared/components/button/HTFButtonComponent';
import { Skeleton } from '@/components/ui/skeleton';

import { useTemplatesListing } from '../services_and_hooks/useTemplatesHook';
import { Template } from '../templates.interface';
import { TemplateCardComponent } from './TemplateCardComponent';
import { TemplateFilterComponent } from './TemplateFilterComponent';

const ITEMS_PER_PAGE = 9;

// Helper function to get category config for section headers
const getCategoryHeaderConfig = (categorySlug?: string | null) => {
  switch (categorySlug) {
    case 'prompt-techniques':
      return { 
        icon: Lightbulb, 
        label: 'Prompt Techniques',
        description: 'Advanced prompting strategies and methodologies'
      };
    case 'agent-frameworks':
      return { 
        icon: Code, 
        label: 'Agent Frameworks',
        description: 'Frameworks and architectures for building AI agents'
      };
    case 'agentic-systems':
      return { 
        icon: Briefcase, 
        label: 'Agentic Systems',
        description: 'Complete systems and workflows for autonomous AI'
      };
    case 'knowledge-management':
      return { 
        icon: BookOpen, 
        label: 'Knowledge Management',
        description: 'Tools and techniques for managing AI knowledge bases'
      };
    default:
      return { 
        icon: FileText, 
        label: 'General Templates',
        description: 'Miscellaneous templates and resources'
      };
  }
};

interface TemplateListComponentProps {
  initialSearch?: string;
  initialCategory?: string;
}

export function TemplateListComponent({ 
  initialSearch = '',
  initialCategory = 'all'
}: TemplateListComponentProps) {
  const [searchTerm] = useQueryState('search', { defaultValue: initialSearch });
  const [category] = useQueryState('category', { defaultValue: initialCategory });

  const {
    templates,
    totalCount,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTemplatesListing(ITEMS_PER_PAGE);

  // Group templates by category only for display purposes (not used in new flat layout)
  const groupedTemplates = templates.reduce((acc, template) => {
    const categorySlug = template.template_categories?.slug || 'uncategorized';
    if (!acc[categorySlug]) {
      acc[categorySlug] = [];
    }
    acc[categorySlug].push(template);
    return acc;
  }, {} as Record<string, Template[]>);

  const renderTemplatesByCategory = () => {
    if (!templates || templates.length === 0) {
      if (isLoading) {
        return <TemplateListSkeleton />;
      }
      return (
        <div className="text-center py-12">
          <p className="text-gray-600">
            {searchTerm || (category && category !== 'all') 
              ? 'No templates found matching your criteria.'
              : 'No templates available at the moment.'
            }
          </p>
        </div>
      );
    }

    // Always show flat grid layout - this looks much better and is more user-friendly
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <TemplateCardComponent key={template.id} template={template} />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Filter Component */}
      <TemplateFilterComponent 
        initialSearch={initialSearch}
        initialCategory={initialCategory}
      />

      {/* Results count */}
      {(totalCount > 0 || templates.length > 0) && (
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">
            {totalCount > 0 ? (
              `Showing ${templates.length} of ${totalCount} templates`
            ) : (
              `${templates.length} template${templates.length !== 1 ? 's' : ''}`
            )}
            {searchTerm && ` for "${searchTerm}"`}
            {category && category !== 'all' && ` in ${category.replace('-', ' ')}`}
          </p>
        </div>
      )}

      {/* Templates Grid */}
      {renderTemplatesByCategory()}

      {/* Load More Button */}
      {hasNextPage && (
        <div className="mt-10 text-center">
          <HTFButton
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            variant="primary-outline"
            size="default"
          >
            {isFetchingNextPage ? 'Loading more...' : 'Load More Templates'}
          </HTFButton>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">
            Error loading templates: {error?.message || 'An unknown error occurred.'}
          </p>
        </div>
      )}
    </div>
  );
}

// Skeleton component for loading state
function TemplateListSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
        <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden h-[280px]">
          <Skeleton className="w-full h-1.5" />
          <div className="p-4 space-y-3">
            <div className="flex justify-between items-start">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-6 w-16" />
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
            <div className="mt-auto pt-4 space-y-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-6 w-24" />
              </div>
              <div className="flex gap-1">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-20" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
